/**
 * ActionError - Error state component for action renderers.
 *
 * Displays error messages when action rendering fails.
 */

import { AlertCircle } from '@untitledui/icons';

import type { RenderMode } from '../../../lib/proposals/registry';

interface ActionErrorProps {
  message: string;
  mode?: RenderMode;
  className?: string;
  /** Optional callback to retry the failed operation */
  onRetry?: () => void;
}

/**
 * Error state display for action renderers.
 * Shows an error icon, message, and optional retry button.
 */
export function ActionError({
  message,
  mode = 'full',
  className = '',
  onRetry,
}: ActionErrorProps) {
  // Inline mode - minimal error display
  if (mode === 'inline') {
    return (
      <span className={`text-error-600 text-sm ${className}`} title={message}>
        Error
      </span>
    );
  }

  // Compact mode - icon and short message
  if (mode === 'compact') {
    return (
      <div className={`flex items-center gap-2 text-error-600 ${className}`}>
        <AlertCircle className='w-4 h-4 shrink-0' aria-hidden='true' />
        <span className='text-sm truncate'>{message}</span>
      </div>
    );
  }

  // Header mode - styled for accordion headers
  if (mode === 'header') {
    return (
      <div className={`flex items-center gap-2 text-error-600 ${className}`}>
        <AlertCircle className='w-5 h-5 shrink-0' aria-hidden='true' />
        <span className='text-sm font-medium'>{message}</span>
      </div>
    );
  }

  // Full/readonly mode - complete error card
  return (
    <div
      className={`border border-utility-error-200 rounded-md bg-utility-error-50 p-4 ${className}`}
      role='alert'
    >
      <div className='flex items-start gap-3'>
        <AlertCircle
          className='w-5 h-5 text-utility-error-600 shrink-0 mt-0.5'
          aria-hidden='true'
        />
        <div className='flex-1 min-w-0'>
          <h4 className='text-sm font-medium text-utility-error-700'>
            Error loading action
          </h4>
          <p className='mt-1 text-sm text-utility-error-700'>{message}</p>
          {onRetry && (
            <button
              type='button'
              onClick={onRetry}
              className='mt-3 text-sm font-medium text-utility-error-700 hover:text-utility-error-600 underline'
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
